import { theme } from 'antd';
import {
	Button,
	Dropdown,
	Empty,
	Flex,
	Table,
	Tag,
	Tooltip,
	Typography,
	Input,
	Row,
	Col,
	Card,
	Divider
} from 'zui/Antd';
import { useDispatch, useSelector } from 'react-redux';
import { selectRoles } from 'reduxStore/roles/selector';
import React, { useEffect, useMemo, useState } from 'react';
import { rolesConfig } from 'config/rolesConfig';
import { formatDisplayName } from 'library/helpers/utility';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectSites } from 'reduxStore/sites/selectors';
import { DeletedFilled, SearchIcon } from 'assets/images/svg';
import { PersonProfileImage, TableFilterDropdown } from 'zComponents/atoms';
import _ from 'lodash';
import {
	PlusOutlined,
	QuestionCircleOutlined,
	GlobalOutlined,
	EnvironmentOutlined,
	ReadOutlined,
	EyeOutlined
} from '@ant-design/icons';
import AssignRoleModal from './AssignRoleModal';
import RevokeRoleModal from './RevokeRoleModal';
import { isCacheExpired } from 'config/cache.config';
import { roleActions } from 'reduxStore/roles/slice';
import { useManagePermittedRoles } from 'library/hooks';

const { Text, Title } = Typography;

const MODAL_KEYS = {
	GLOBAL_ADMIN: 'globalAdmin',
	SITE_ADMIN: 'siteAdmin',
	GLOBAL_READ_ONLY: 'globalReadOnly',
	SITE_READ_ONLY: 'siteReadOnly'
};

const ROLE_META = {
	[rolesConfig.GLOBAL_ADMIN]: { label: 'Global Admin', color: 'blue' },
	[rolesConfig.SITE_ADMIN]: { label: 'Site Admin', color: 'geekblue' },
	[rolesConfig.GLOBAL_READ_ONLY]: { label: 'Global Read Only', color: 'default' },
	[rolesConfig.SITE_READ_ONLY]: { label: 'Site Read Only', color: 'default' }
};

function RolesSettings() {
	const {
		token: { fontSizeSM, colorTextSecondary, colorTextDescription, paddingMD }
	} = theme.useToken();
	const dispatch = useDispatch();

	// 📦 Redux state — filtered by site for site-scoped admins
	const people = useSelector(selectPeople);
	const peopleLoading = useSelector((state) => state.People.loading);
	const roles = useSelector(selectRoles);
	const sites = useSelector(selectSites);
	const { orgID, roles: userRoles = {} } = useSelector((state) => state.Auth.selectedProfile);
	const userRoleIDs = Object.keys(userRoles).map(Number);
	const isGlobalAdmin = userRoleIDs.some((r) => [rolesConfig.GLOBAL_ADMIN, rolesConfig.DEVELOPER].includes(r));
	const {
		data: rolesData,
		rolesLoadingComplete,
		loadingAssignRole,
		loading: rolesLoading,
		timeStamp: rolesTimeStamp
	} = useSelector((state) => state.Roles);

	// 🌀 Local state
	const [openModal, setOpenModal] = useState(null);
	const [searchText, setSearchText] = useState('');
	const [inputValue, setInputValue] = useState('');
	const [openRevokeModal, setOpenRevokeModal] = useState(false);
	const [revokePersonID, setRevokePersonID] = useState(null);
	const [revokePermissionID, setRevokePermissionID] = useState(null);

	// ✅ Permission checks
	const canAssignRole = useManagePermittedRoles('assignRole');
	const canRevokeRole = useManagePermittedRoles('revokeRole');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	useEffect(() => {
		if (isCacheExpired(rolesTimeStamp, 1) || (!rolesLoadingComplete && !rolesLoading) || !(orgID in rolesData)) {
			dispatch(roleActions.loadRoles());
		}
	}, [dispatch, orgID, rolesData, rolesLoadingComplete, rolesLoading, rolesTimeStamp]);

	// ✅ Debounced search handler
	const debouncedSearch = _.debounce((searchString) => {
		setSearchText(searchString);
	}, 300);

	const handleChangeInput = (e) => {
		const value = e.target.value;
		setInputValue(value);
		debouncedSearch(value);
	};

	// ✅ Build grouped list of role rows for the table
	const tableData = useMemo(() => {
		const adminPermissionIDs = [
			rolesConfig.GLOBAL_ADMIN,
			rolesConfig.SITE_ADMIN,
			rolesConfig.GLOBAL_READ_ONLY,
			rolesConfig.SITE_READ_ONLY
		];

		const adminRoles = roles.filter((r) => adminPermissionIDs.includes(r.permissionID));
		const groupedRolesMap = new Map();

		adminRoles.forEach((r) => {
			const key = `${r.personID}-${r.permissionID}`;

			if (!groupedRolesMap.has(key)) {
				groupedRolesMap.set(key, {
					personID: r.personID,
					permissionID: r.permissionID,
					scopes: []
				});
			}

			const group = groupedRolesMap.get(key);

			// Resolve scope display name
			let scopeName = 'All Sites';
			if (r.permissionID === rolesConfig.SITE_ADMIN || r.permissionID === rolesConfig.SITE_READ_ONLY) {
				const site = sites.find((s) => s.siteID === r.entityID);
				scopeName = site?.name || 'Unknown Site';
			}

			if (!group.scopes.includes(scopeName)) {
				group.scopes.push(scopeName);
			}
		});

		return Array.from(groupedRolesMap.values()).map((group) => {
			const person = people.find((p) => p.personID === group.personID);
			return {
				key: `${group.personID}-${group.permissionID}`,
				personID: group.personID,
				permissionID: group.permissionID,
				name: formatDisplayName(person)?.displayName || '-',
				email: person?.email || '-',
				photoUrl: person?.photoUrl || undefined,
				role: ROLE_META[group.permissionID]?.label || '-',
				roleColor: ROLE_META[group.permissionID]?.color || 'default',
				scopeDisplay: group.scopes.join(', '),
				rawScopes: group.scopes
			};
		});
	}, [roles, people, sites]);

	// ✅ Apply text search filtering
	const filteredTableData = useMemo(() => {
		return tableData.filter((row) => {
			return (
				!searchText.length ||
				[row.name, row.email].some((field) => field?.toLowerCase().includes(searchText.toLowerCase()))
			);
		});
	}, [tableData, searchText]);

	// ✅ Filter options for dropdowns
	const roleOptions = useMemo(() => {
		const uniqueRoles = [...new Set(tableData.map((item) => item.role))];
		return uniqueRoles.map((role) => ({ label: role, value: role })).sort((a, b) => a.label.localeCompare(b.label));
	}, [tableData]);

	const scopeOptions = useMemo(() => {
		const uniqueScopes = new Set();
		tableData.forEach((item) => item.rawScopes.forEach((s) => uniqueScopes.add(s)));
		return Array.from(uniqueScopes)
			.map((scope) => ({ label: scope, value: scope }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [tableData]);

	// ✅ Columns definition
	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			width: 350,
			render: (name, record) => (
				<Flex align='center' gap={12}>
					<PersonProfileImage
						size={42}
						photoUrl={record.photoUrl}
						name={name}
						entityName={'People'}
						shape={'circle'}
						edit={false}
					/>
					<Flex vertical gap={0}>
						<Text strong style={{ lineHeight: '22px' }}>
							{name}
						</Text>
						<Text style={{ fontSize: fontSizeSM, color: colorTextSecondary, lineHeight: '20px' }}>
							{record.email}
						</Text>
					</Flex>
				</Flex>
			),
			sorter: (a, b) => a.name.localeCompare(b.name)
		},
		{
			title: 'Role',
			dataIndex: 'role',
			key: 'role',
			render: (role, record) => <Tag color={record.roleColor}>{role}</Tag>,
			onFilter: (value, record) => record.role === value,
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={roleOptions} placeholder='Search roles' />
			)
		},
		{
			title: 'Scope',
			dataIndex: 'scopeDisplay',
			key: 'scope',
			render: (scopeDisplay) => <Text style={{ color: colorTextSecondary }}>{scopeDisplay}</Text>,
			onFilter: (value, record) => record.rawScopes.includes(value),
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={scopeOptions} placeholder='Search scopes' />
			)
		},
		{
			title: 'Actions',
			key: 'actions',
			align: 'right',
			width: 100,
			render: (_, record) => (
				<Tooltip title={!canRevokeRole ? permissionDeniedMessage : 'Remove Role'}>
					<Button
						icon={<DeletedFilled width={16} height={16} color='currentColor' />}
						type='text'
						disabled={!canRevokeRole || loadingAssignRole}
						onClick={() => {
							setRevokePersonID(record.personID);
							setRevokePermissionID(record.permissionID);
							setOpenRevokeModal(true);
						}}
					/>
				</Tooltip>
			)
		}
	];

	const dropdownItems = [
		// Global roles — only visible to global admins
		...(isGlobalAdmin
			? [
					{
						key: MODAL_KEYS.GLOBAL_ADMIN,
						label: 'Global Admin',
						icon: <GlobalOutlined />,
						onClick: () => setOpenModal(MODAL_KEYS.GLOBAL_ADMIN)
					},
					{
						key: MODAL_KEYS.GLOBAL_READ_ONLY,
						label: 'Global Read Only',
						icon: <ReadOutlined />,
						onClick: () => setOpenModal(MODAL_KEYS.GLOBAL_READ_ONLY)
					},
					{ type: 'divider' }
				]
			: []),
		// Site roles — visible to all admins
		{
			key: MODAL_KEYS.SITE_ADMIN,
			label: 'Site Admin',
			icon: <EnvironmentOutlined />,
			onClick: () => setOpenModal(MODAL_KEYS.SITE_ADMIN)
		},
		{
			key: MODAL_KEYS.SITE_READ_ONLY,
			label: 'Site Read Only',
			icon: <EyeOutlined />,
			onClick: () => setOpenModal(MODAL_KEYS.SITE_READ_ONLY)
		}
	];

	return (
		<Flex vertical gap={24}>
			{/* Header */}
			<Flex vertical>
				<Flex align='center' gap={6}>
					<Title level={4} style={{ margin: 0 }}>
						Roles
					</Title>
					<Tooltip title='Manage who has admin or read-only access across the platform.'>
						<QuestionCircleOutlined style={{ color: colorTextSecondary, fontSize: 16 }} />
					</Tooltip>
				</Flex>
				<Divider style={{ margin: '16px 0px 0px' }} />
			</Flex>

			{/* Search Bar & Add Button */}
			<Row justify='space-between'>
				<Col span={10}>
					<Input
						placeholder='Search People by Name or Email'
						value={inputValue}
						allowClear
						onChange={handleChangeInput}
						onClear={() => setInputValue('')}
						suffix={<SearchIcon color={colorTextDescription} />}
					/>
				</Col>
				<Col>
					<Dropdown menu={{ items: dropdownItems }} trigger={['click']} disabled={!canAssignRole}>
						<Tooltip title={!canAssignRole ? permissionDeniedMessage : 'Add Role'} placement='top'>
							<Button type='primary' icon={<PlusOutlined />} disabled={!canAssignRole}>
								Add Role
							</Button>
						</Tooltip>
					</Dropdown>
				</Col>
			</Row>

			{/* Roles Table */}
			<Card styles={{ body: { padding: paddingMD } }}>
				<Table
					columns={columns}
					dataSource={filteredTableData}
					pagination={{
						defaultPageSize: 10,
						showQuickJumper: true,
						showSizeChanger: false
					}}
					bordered={false}
					locale={{
						emptyText: (
							<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No admin roles assigned yet.' />
						)
					}}
					size='small'
					loading={loadingAssignRole || rolesLoading || peopleLoading}
				/>
			</Card>

			<AssignRoleModal
				visible={!!openModal}
				setVisible={(val) => {
					if (!val) setOpenModal(null);
				}}
				roleType={openModal}
			>
				<div />
			</AssignRoleModal>

			<RevokeRoleModal
				open={openRevokeModal}
				setOpen={(val) => {
					setOpenRevokeModal(val);
					if (!val) {
						setRevokePersonID(null);
						setRevokePermissionID(null);
					}
				}}
				personID={revokePersonID}
				permissionID={revokePermissionID}
			/>
		</Flex>
	);
}

export default RolesSettings;
